import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, pipe, Subject } from "rxjs";
import { finalize, map, switchMap, take, tap } from "rxjs/operators";
import { BaseResponse } from "src/app/models/base-response.model";
import { SearchTableParam } from "src/app/models/base-search.model";
import { NewSiteCulture, SiteCreate, SiteCulture, SiteCultureGetAllResponse, SiteGetAllResponse, SiteResponse, SiteSearch, SitesListRespone, SiteUpdate, SiteUpsert } from "../models/data-models";
import { SiteHostName } from "../models/host-name.models";
import { SiteSetting } from "../models/site-setting.models";


@Injectable({
  providedIn: "root",
})
export class SiteService {
  private SiteChangedSubject = new Subject<boolean>();
  isSiteChanged$: Observable<boolean> = this.SiteChangedSubject.asObservable();

  private siteCultureSubject = new BehaviorSubject<BaseResponse<SiteCultureGetAllResponse[]>>(null);
  siteCulture$: Observable<BaseResponse<SiteCultureGetAllResponse[]>> = this.siteCultureSubject.asObservable();

  private usedSiteCultureSubject = new BehaviorSubject<NewSiteCulture[]>(null);
  usedSiteCulture$: Observable<NewSiteCulture[]> = this.usedSiteCultureSubject.asObservable();

  private siteCategoriesSubject = new BehaviorSubject<NewSiteCulture[]>(null);
  siteCategories$: Observable<NewSiteCulture[]> = this.siteCategoriesSubject.asObservable();

  // private allUserSitesSubject = new BehaviorSubject<any[]>(null);
  // allUserSites$: Observable<any[]> = this.allUserSitesSubject.asObservable();


  constructor(private http: HttpClient) {
    if (localStorage.getItem('userData')) {
      this.updateSitesChanged();
    }

    this.getUsedCulturs()
      .pipe(
        switchMap((res) =>
          this.getSiteCategories()
        )
      )
      .subscribe()

    this.isSiteChanged$.pipe(
      switchMap((res) =>
        this.getUsedCulturs()
      ),
      switchMap((res) =>
        this.getSiteCategories()
      )
    ).subscribe()
  }


  //////////// Start Of New Functions //////////////////


  upsert(request) {
    return this.http.post<BaseResponse<number>>("/Sites/Upsert", request);
  }

  upsertHostNamesWithCultures(request) {
    return this.http.post<BaseResponse<any>>("/Sites/UpsertHostNames", request);
  }

  getSiteMetadata(siteId): Observable<BaseResponse<any>> {
    return this.http.get<BaseResponse<any>>(`/Sites/GetMetaDataWithCulture/${siteId}`)
  }

  getSiteScripts(siteId): Observable<BaseResponse<any>> {
    return this.http.get<BaseResponse<any>>(`/Sites/GetSiteScripts/${siteId}`)
  }

  updateSiteScript(siteId, payload) {
    return this.http.post(`/Sites/AddSiteScripts`, {
      siteId: siteId,
      script: payload
    })
  }

  clearCache() {
    return this.http.post(`/Cashing/ClearCache`, {})
  }

  updateSiteMetadata(siteId, payload) {
    return this.http.post(`/Sites/AddMetaDataToHostName/${siteId}`, payload)
  }

  public getUsedCulturs(siteId?: number) {
    return this.http.get<NewSiteCulture[]>(
      `/Sites/GetUsedSiteCultures/${siteId || 0}`
    ).pipe(
      tap((res: NewSiteCulture[]) => res && this.usedSiteCultureSubject.next(res))
    )
  }
public GetUsedSiteCulturesByStatus(){
  return this.http.get("/Sites/GetUsedSiteCulturesByStatus/2")
}
  //////////// End Of New Functions //////////////////

  public completeAdd(request: SiteCreate) {
    return this.http.post<BaseResponse<number>>(
      "/Sites/CompleteAddSite",
      request
    );
  }

  public addSiteCultures(request: SiteCulture[]) {
    return this.http.post<BaseResponse<boolean>>("/Sites/AddSiteCultures", request);
  }

  // public getSites() {
  //   const current = this.allUserSitesSubject.getValue();
  //   if (current) {
  //     return of(current)
  //   }
  //   return this.http.get("/Sites/GetAllSites").pipe(
  //     tap((res: any[]) => res && this.allUserSitesSubject.next(res)),

  //   );
  // }
  // reset() {
  //   this.allUserSitesSubject.next(null);
  //   this.getSites().subscribe();
  // }
  public addSiteCulture(request: SiteCulture) {
    return this.http.post<BaseResponse<boolean>>("/SiteCulture/Add", request);

  }

  public addSiteHostName(request: SiteHostName) {
    return this.http.post<BaseResponse<number>>("/Sites/AddSiteHostName", request);
  }

  public editSiteHostName(request: SiteHostName) {
    return this.http.post<BaseResponse<boolean>>("/Sites/EditSiteHostName", request);
  }

  public addSiteSetting(request: SiteSetting) {
    return this.http.post<BaseResponse<boolean>>("/Sites/AddSiteSettings", request);
  }

  public editSiteSetting(request: SiteSetting) {
    return this.http.post<BaseResponse<boolean>>("/Sites/EditSiteSettings", request);
  }

  public update(request: SiteUpdate) {
    return this.http.post<BaseResponse<number>>("/Sites/Edit", request);
  }

  public getList(request: SearchTableParam<SiteSearch>) {
    return this.http.post<SitesListRespone>("/Sites/GetList", request);
  }

  public getAllPages() {
    return this.http.post<BaseResponse<any>>("/Pages/GetAllPages",{});
  }

  public getCoreCompanies() {
    return this.http.get<BaseResponse<any>>(
      "/EskaCoreIntegrationApi/GetAllCompanies"
    );
  }

  public getAddresses() {
    return this.http.get<BaseResponse<any>>("/UserAdress/GetAllAddresses");
  }

  public get(id: number) {
    return this.http.get<any>(`/Sites/GetSiteDetails/${id}`);
  }

  public delete(id: number) {
    return this.http.delete<BaseResponse<number>>(`/Sites/DeleteSite/${id}`);
  }

  public deleteSiteCulture(id: number) {
    return this.http.get<BaseResponse<number>>(`/SiteCulture/Delete?Id=${id}`);
  }

  public deleteHost(id: number) {
    return this.http.get<BaseResponse<boolean>>(`/Sites/DeleteSiteHost/${id}`);
  }

  public deleteSetting(id: string) {
    return this.http.get<BaseResponse<boolean>>(
      `/Sites/DeleteSiteSettings/${id}`
    );
  }

  public setSiteAsDefault(site: SiteResponse) {
    return this.http.post<BaseResponse<boolean>>(
      `/Sites/SetSiteAsDefault`,
      site
    );
  }

  public getAll() {
    return this.http.get<BaseResponse<SiteGetAllResponse[]>>(`/Sites/GetAll`);
  }

  public getSiteCultures(siteId?: number) {
    // let url = `/SiteCulture/GetCulturesBySiteId`;
    // url = siteId? `/SiteCulture/GetCulturesBySiteId/`: url;
    return this.http.get<BaseResponse<SiteCultureGetAllResponse[]>>(
      `/SiteCulture/GetCulturesBySiteId/${siteId}`
    );
  }

  // public getUsedCulturs(siteId?: number) {
  //   return this.http.get<BaseResponse<SiteCultureGetAllResponse[]>>(
  //     `/Sites/GetUsedSiteCultures/${siteId || 0}`
  //   ).pipe(
  //     tap(res => res && this.usedSiteCultureSubject.next(res))
  //   )
  // }


  public getSiteDetails(id: number) {
    return this.http.get<any>(`/Sites/GetSiteDetails/${id}`);
  }

  public getAllCultures() {
    return this.siteCulture$.pipe(
      take(1),
      switchMap(res => {
        if (res) { return of(res); }
        else { return this.http.get<BaseResponse<any[]>>("/currency/get-used-cultures") }
      }),
      tap(res => res && this.siteCultureSubject.next(res))
    )
  }

  // public GetCategoriesbySiteId(data) {
  //   return this.http.post<any>(`/Categories/GetCategoriesbySiteId`, data);
  // }
  public getCategoriesBySiteId(data) { 
    return this.http.post<any>("/CategoriesApi/GetCategoriesbySiteId",data)
  }

  public getSiteCategories() {
    return this.http.get<any>(`/CategoriesApi/GetCategoriesType`).pipe(
      tap((res: any[]) => res && this.siteCategoriesSubject.next(res))
    )
  }

  public updateCategory(data) {
    return this.http.post<any>(`/Categories/AddCategory`, data);
  }

  public deleteCategory(catId) {
    return this.http.get<any>(`/Categories/ChangeCategoryStatus/${catId}/3`);
  }


  public updateSitesChanged() {
    this.SiteChangedSubject.next(true);
  }
}
